/**
 * App context (task 20.3): makes the resolved runtime config, the API client,
 * and the auth facade available to every screen. Built once at startup after
 * config is loaded and the user has signed in.
 */

import { createContext, useContext, type ReactNode } from "react";
import type { RufusRuntimeConfig } from "./config.js";
import type { ApiClient } from "./api-client.js";
import type { CognitoAuth } from "./auth.js";

export interface AppServices {
  readonly config: RufusRuntimeConfig;
  readonly api: ApiClient;
  readonly auth: CognitoAuth;
}

const AppContext = createContext<AppServices | null>(null);

export function AppProvider({
  services,
  children,
}: {
  readonly services: AppServices;
  readonly children: ReactNode;
}) {
  return <AppContext.Provider value={services}>{children}</AppContext.Provider>;
}

export function useServices(): AppServices {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useServices must be used within an AppProvider.");
  return ctx;
}
